import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from 'antd';
import { StarIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';
import Notification from '../../../components/notification/notification';
import {
  apiCustomerOrderFeedback,
  apiGetCustomerOrderFeedbackByOrder,
} from '../../../services/FeedbackApi';
import { TWButton } from '../../../components/ui';

const RATING_LABELS = [
  'Not great',
  'Could be better',
  'Pretty good',
  'Great',
  'Loved it',
];

// A star link in the receipt mail sends its rating without a further tap.
// The wait lets mail security scanners that open links in a hidden page pass
// by without rating the order.
const AUTO_SUBMIT_DELAY_MS = 1200;

// thisway-api's refusal of a second rating for one order
// (OrderFeedbackRules.cs, served as a 400 { StatusCode, Message } body by
// ExceptionMiddleware).
const ALREADY_RATED = 'Feedback already exists for this order.';

const validStars = (value) =>
  Number.isInteger(value) && value >= 1 && value <= 5 ? value : 0;

const isAlreadyRated = (error) => {
  const response = error?.response;
  if (response?.status !== 400) return false;
  const data = response.data;
  const message =
    typeof data === 'string' ? data : data?.Message || data?.message;
  return typeof message === 'string' && message.trim() === ALREADY_RATED;
};

const Rating = ({ order, initialRating }) => {
  // Projede kullanılan yeşil — Tailwind green-600, receipt'teki "Paid"
  // damgasıyla aynı ton.
  const accentColor = '#16a34a';

  // initialRating comes from the receipt mail's star link (?stars=N).
  const autoStars = validStars(initialRating);

  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [showTextInput, setShowTextInput] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasFeedback, setHasFeedback] = useState(false);
  const [lookupSettled, setLookupSettled] = useState(false);
  // True from mount while the mail's star is still to be sent (lookup, wait,
  // request). It ends in thanks, in the form (failure or a tapped star), or in
  // the stored rating.
  const [autoPending, setAutoPending] = useState(autoStars > 0);
  const autoSentRef = useRef(false);
  const [notf, setNotf] = useState({
    display: false,
    type: 'success',
    message: '',
  });

  const showStored = useCallback((data) => {
    setHasFeedback(Boolean(data.feedbackText));
    setRating(data.starCount || 0);
    setFeedback(data.feedbackText || '');
    setSubmitted(true);
  }, []);

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const response = await apiGetCustomerOrderFeedbackByOrder(order.id);
        if (response?.data) {
          setAutoPending(false);
          showStored(response.data);
        } else {
          setLookupSettled(true);
        }
      } catch (error) {
        // The API answers 404 when the order has no feedback; any failure
        // leaves the customer able to rate.
        setLookupSettled(true);
      }
    };
    if (order.id) fetchFeedback();
  }, [order.id, showStored]);

  useEffect(() => {
    if (!lookupSettled || !autoPending || autoSentRef.current) return undefined;

    const send = async () => {
      autoSentRef.current = true;
      try {
        await apiCustomerOrderFeedback({
          orderid: order.id,
          starcount: autoStars,
          feedbacktext: '',
          storeid: order.storeid,
          customerid: order.customerid,
        });
        setRating(autoStars);
        setFeedback('');
        setHasFeedback(false);
        setSubmitted(true);
      } catch (error) {
        if (isAlreadyRated(error)) {
          // Rated in the meantime (another tab, a second tap in the mail):
          // show what is stored.
          try {
            const response = await apiGetCustomerOrderFeedbackByOrder(order.id);
            if (response?.data) showStored(response.data);
            else setSubmitted(true);
          } catch (lookupError) {
            setSubmitted(true);
          }
        } else {
          setRating(autoStars);
          setShowTextInput(true);
          setNotf({
            display: true,
            type: 'error',
            message: 'Error submitting feedback. Please try again.',
          });
        }
      } finally {
        setAutoPending(false);
      }
    };

    let timer = null;
    const cancel = () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };
    const arm = () => {
      if (
        timer === null &&
        !autoSentRef.current &&
        document.visibilityState === 'visible'
      ) {
        timer = setTimeout(() => {
          timer = null;
          if (document.visibilityState === 'visible' && !autoSentRef.current) {
            send();
          }
        }, AUTO_SUBMIT_DELAY_MS);
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') arm();
      else cancel();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    arm();
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      cancel();
    };
  }, [
    lookupSettled,
    autoPending,
    autoStars,
    order.id,
    order.storeid,
    order.customerid,
    showStored,
  ]);

  const handleRatingClick = (selectedRating) => {
    if (autoPending) {
      // Once the mail's star is on its way, it is the answer.
      if (autoSentRef.current) return;
      // A star tapped before it is sent: the customer rates by hand.
      setAutoPending(false);
    }
    if (!submitted) {
      setRating(selectedRating);
      setShowTextInput(true);
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) return;
    setLoading(true);
    try {
      await apiCustomerOrderFeedback({
        orderid: order.id,
        starcount: rating,
        feedbacktext: feedback,
        storeid: order.storeid,
        customerid: order.customerid,
      });
      setSubmitted(true);
      setShowTextInput(false);
      setNotf({
        display: true,
        type: 'success',
        message: 'Thank you for your feedback!',
      });
    } catch (error) {
      setNotf({
        display: true,
        type: 'error',
        message: 'Error submitting feedback. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const activeValue = autoPending ? autoStars : hover || rating;
  const activeLabel = activeValue > 0 ? RATING_LABELS[activeValue - 1] : null;

  // Submitted state — confirmation
  if (submitted) {
    return (
      <div className="text-center" aria-label="Your feedback">
        <div
          className="mx-auto flex h-10 w-10 items-center justify-center rounded-full"
          style={{
            backgroundColor: accentColor + '14',
            color: accentColor,
          }}
        >
          <CheckCircleIcon className="h-6 w-6" />
        </div>
        <p className="mt-3 text-base font-semibold text-slate-900 dark:text-slate-100">
          Thanks for your feedback
        </p>
        {rating > 0 && (
          <div
            className="mt-2 flex justify-center gap-1"
            role="img"
            aria-label={`${rating} out of 5 stars`}
          >
            {[...Array(5)].map((_, index) => {
              const isActive = index + 1 <= rating;
              return isActive ? (
                <StarIcon
                  key={index}
                  className="h-5 w-5"
                  style={{ color: accentColor }}
                />
              ) : (
                <StarOutlineIcon
                  key={index}
                  className="h-5 w-5 text-slate-300 dark:text-slate-600"
                  strokeWidth={1.5}
                />
              );
            })}
          </div>
        )}
        {hasFeedback && feedback && (
          <p className="mx-auto mt-3 max-w-xs text-sm italic leading-relaxed text-slate-600 dark:text-slate-300">
            “{feedback}”
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="text-center" aria-label="Rate your order">
      <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
        Rate your experience
      </h3>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Your feedback helps us improve.
      </p>

      <div className="mt-5 flex justify-center gap-1.5">
        {[...Array(5)].map((_, index) => {
          const starValue = index + 1;
          const isActive = activeValue >= starValue;
          return (
            <button
              key={index}
              type="button"
              aria-label={`${starValue} star${starValue > 1 ? 's' : ''}`}
              onClick={() => handleRatingClick(starValue)}
              onMouseEnter={() => !submitted && !autoPending && setHover(starValue)}
              onMouseLeave={() => !submitted && !autoPending && setHover(0)}
              className="cursor-pointer rounded-md bg-transparent p-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
              style={{ outlineColor: accentColor }}
            >
              {isActive ? (
                <StarIcon
                  className="h-7 w-7"
                  style={{ color: accentColor }}
                />
              ) : (
                <StarOutlineIcon
                  className="h-7 w-7 text-slate-300 dark:text-slate-600"
                  strokeWidth={1.5}
                />
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-2 flex h-5 items-center justify-center">
        {activeLabel && (
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {activeLabel}
          </p>
        )}
      </div>

      {autoPending && (
        <p className="mt-4 text-sm text-slate-500 dark:text-slate-400" role="status">
          Sending your rating…
        </p>
      )}

      {!autoPending && showTextInput && (
        <div className="mx-auto mt-4 max-w-sm space-y-3 text-left">
          <Input.TextArea
            className="resize-none rounded-md border-slate-300 bg-white text-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200"
            rows={3}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Tell us about your experience (optional)…"
          />
          <TWButton
            variant="primary"
            block
            label={loading ? 'Sending…' : 'Submit feedback'}
            disabled={loading || rating === 0}
            loading={loading}
            onClick={handleSubmit}
            style={{ backgroundColor: accentColor }}
          />
        </div>
      )}

      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </div>
  );
};

export default Rating;
